<template lang="pug">
  .card-container.column(v-if='!!resource' :class="{ 'no-margin': !!noMargin }")
    .card-info.item-1.relative.column.space-between
      .color-tag(:style='setTagColor()' :class='{krea: isKrea}')
      .column.info-container
        .row.item-1.self-end.middle.m-bottom-05
          .row.middle(v-if='languages.length')
            img.m4-right-2.img-size(
              v-for='language in languages'
              :src='language.icon'
              :alt='$t(`languages.${language.translation}`)'
              :title='$t(`languages.${language.translation}`)'
            )
        router-link.item-4.text-big.black.bold.card-title.m4-bottom-2.text-left(
          :to='getResourceLink()'
          @click='goToResource'
        ) {{ resource.name || $t('resourceCard.untitled') }}
        .used-content.row.middle.m-bottom-1(v-if='resource.usedResource' @click='showUsedContentDrawer')
          simple-svg.fern-green-icon.m-right-05(:filepath='checkIcon' width='14px' height='14px')
          span.text-small.fern-green.underline.pointer.m-left-05 {{ $t("programCard.seeUsedRecord") }}
        .row.item-1.m-bottom-1(v-if='!isTeachersTraining')
          span.text-small.age-desc(:style='setAgeColor(resource.ageRange.mainColor)')
            | {{ $t('resourceCard.agesTag', { min: resource.ageRange.minAge, max: resource.ageRange.maxAge }) }}
          span.text-small.age-desc(v-if='resource.secondaryAgeRange' :style='setAgeColor(resource.secondaryAgeRange.mainColor)')
            | {{ $t('resourceCard.agesTag', { min: resource.secondaryAgeRange.minAge, max: resource.secondaryAgeRange.maxAge}) }}
        .row.m-bottom-1(v-if='isTeachersTraining' :style='teachersTrainingBadge')
          span.relative.teachers-training-badge.text-small {{ $t('resourceCard.category.teachersTraining') }}
        .row.item-2.middle.m-top-auto
          .row.middle(v-if='associatedEducationalProgramName && !isKrea')
            simple-svg.blue-icon.m-right-1(:filepath='programIcon' :height='"15px"' :width='"15px"')
            span.text-small.blue
              | {{ associatedEducationalProgramName }}
    .card-footer.row.space-between
      .row.middle(v-if='!isKrea')
        img.authors(:src='resource.author.picture || defaultImage')
        span.text-small.dark-gray
          | {{ $tc('resourceCard.by', resource.author.name) }}
      .row.middle(v-else)
        img.authors(:src='resource.author.picture || defaultImage')
        span.text-small.dark-gray
          | {{ $tc('resourceCard.by', resource.author.name) }}
      button.icon-button(v-if='!isKrea && canRemoveResource' type='button' @click='openRemoveResourceModal' :title='$t("resourceCard.removeResourceFromFolder")')
        simple-svg.dark-gray-icon(:filepath='removeFromFolderIcon' width='20px' height='20px')
      button.icon-button(v-else-if='!isKrea' type='button' @click='openResourceFolderManager' :title='$t("resourceCard.saveResourceInFolder")')
        simple-svg.dark-gray-icon(:filepath='archiveIcon' width='17px' height='17px')
</template>

<script>
import { mapActions, mapState } from 'vuex'

import Stars from './Stars'
import Tag from './Tag'

import programIcon from '@/assets/ic_programs.svg'
import archiveIcon from '@/assets/ic_archive.svg'
import defaultImage from '@/assets/empty_profile.svg'
import removeFromFolderIcon from '@/assets/ic_trash.svg'

import { userPermissions } from '../utils/constants'
import { teachersTrainingName } from '../constants/resource'
import { allLanguages, supportedLanguages } from '../constants/languages'
import { routes } from '../router'
import checkIcon from '@/assets/ic_check.svg'

export default {
  components: { Stars, Tag },
  props: {
    resource: { type: Object, required: true },
    folderId: { type: Number },
    bookmarked: { type: Boolean, value: false },
    noMargin: { type: Boolean, value: false },
    addKreaResource: { type: Boolean },
    preferredLanguage: { type: String }
  },
  data() {
    return {
      checkIcon,
      archiveIcon,
      removeFromFolderIcon,
      programIcon,
      defaultImage
    }
  },
  computed: {
    ...mapState({
      locale: state => state.behavior.locale,
      folderDetail: state => state.folders.folderDetail
    }),
    teachersTrainingBadge() {
      return {
        '--category-color': this.resource.categoryColor
      }
    },
    isTeachersTraining() {
      return this.resource.category === teachersTrainingName
    },
    isKrea() {
      return this.resource.category === 'Krea'
    },
    languages() {
      return supportedLanguages
        .filter(language => this.resource.languages.includes(language.lang))
    },
    associatedEducationalProgramName() {
      return this.resource.educationalProgram ? this.resource.educationalProgram.name : this.resource.educationalProgramName
    },
    canRemoveResource() {
      return this.$route.name === routes.folderContent &&
        (this.folderDetail.userPermission === userPermissions.owner || this.folderDetail.userPermission === userPermissions.edit)
    }
  },
  methods: {
    ...mapActions(['showElement', 'changeInnerLocale']),
    toggleBookmark() {
      if (this.bookmarked) {
        this.showElement({
          element: 'removeResourceModal',
          props: { resourceId: this.resource.id, folderId: this.folderId } })
      }
    },
    showUsedContentDrawer() {
      this.showElement({
        element: 'usedContentDrawer',
        props: { resourceId: this.resource.id }
      })
    },
    getLanguageIcon(language) {
      return allLanguages.find(lang => lang.lang === language).icon
    },
    setTagColor() {
      return {
        '--tag-color': this.resource.categoryColor
      }
    },
    setAgeColor(color) {
      return {
        '--age-color': color || 'white'
      }
    },
    goToResource() {
      this.changeInnerLocale(this.resource.resourceLocale)
    },
    getResourceLink() {
      return this.$route.name === routes.folderContent && this.resource.savedLanguage !== 'None'
        ? { name: routes.resource, params: { id: this.resource.id }, query: { locale: this.preferredLanguage || this.resource.savedLanguage } }
        : { name: routes.resource, params: { id: this.resource.id }, query: { locale: this.preferredLanguage } }
    },
    openResourceFolderManager() {
      this.showElement({
        element: 'addResourceModal',
        props: {
          id: this.resource.id,
          resource: this.resource,
          locale: this.resource.resourceLocale
        }
      })
    },
    openRemoveResourceModal() {
      this.showElement({
        element: 'removeResourceModal',
        props: {
          folderId: this.folderId,
          resourceId: this.resource.id
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../scss/variables/colors';

.card-container{
  height: 260px;
}

.card-container.no-margin {
  margin: 0;
}

.teachers-training-badge {
  display: inline-flex;
  width: auto;
  padding-left: 16px;

  &::before {
    background-color: var(--category-color);
    border-radius: 100%;
    content: '';
    height: 8px;
    left: 0;
    position: absolute;
    top: 6px;
    width: 8px;
  }
}

.color-tag {
  background-color: var(--tag-color);
  border-radius: 4px;
  height: 120px;
  max-width: 48px;
  width: 7px;
  position: absolute;
  left: 0;
  top: 50%;
  border: none;
  transform: translate(-50%, -50%);
  box-shadow: 0px 0px 9px 5px $shadow-gray;
  -webkit-box-shadow: 0px 0px 9px 5px $shadow-gray;
  -moz-box-shadow: 0px 0px 9px 5px $shadow-gray;
  &.krea{
    background: linear-gradient($yellow 33%, $red 33%, $red 66%, $blue 66%);
  }
}

.card-info {
  display: flex;
  flex-direction: column;
  padding: 15px;
  width: 100%;
  .info-container{
    height: 100%;
    align-items: start;
  }
  .age-desc {
    padding-left: 16px;
    position: relative;

    &:not(:last-child) {
      margin-right: 16px;
    }

    &::before {
      background-color: var(--age-color);
      border-radius: 100%;
      content: '';
      height: 8px;
      left: 0;
      position: absolute;
      top: 6px;
      width: 8px;
    }
  }
}

.read-text {
  font-size: 12px;
}

.card-title {
  max-width: 196px;
  font-size: 19px;
}

.card-footer {
  align-items: center;
  border-top: 1px solid $light-gray;
  display: flex;
  justify-content: space-between;
  height: 50px;
  padding: 0 12px;
  width: 100%;
  .authors{
    height: 30px;
    width: 30px;
  }
}

.bookmark {
  height: 25px;
  width: 25px;
}

.m-bottom-12 {
  margin-bottom: 12px;
}

.img-size {
  width: 20px;
}
</style>
